<script>
export const group = 'Full'
</script>

<script setup>
import { getCurrentInstance, ref, computed, onMounted, watch, nextTick } from 'vue'
import useDisappearable from '../../../composables/useDisappearable'
import DefaultBackgroundVideo from '@/assets/videos/Achtergrond_QBH.mp4'
import VideoPlayer from '../../VideoPlayer.vue'
const gsapTimeline = useGsapTimeline()

const props = defineProps({
  item: Object,
})

const shadowBottomEls = ref(null)
const textTopLeftEls = ref(null)
const textTopRightEls = ref(null)
const textBottomEls = ref(null)
const bgImage = ref(null)
const topLineContainer = ref(null)
const topLineFontSizeVw = ref(30.0)

const presenterNames = computed(() => (props.item?.enrichment?.program?.presenterNames || []).map((name) => `${name}`.trim()).filter(Boolean))

const lineTexts = computed(() => {
  if (presenterNames.value.length === 0) {
    return { topSingle: '', topLeft: '', topRight: '', bottom: '' }
  }

  if (presenterNames.value.length === 1) {
    const words = presenterNames.value[0].split(/\s+/).filter(Boolean)
    const firstWord = words[0] || ''

    return {
      topSingle: firstWord,
      topLeft: '',
      topRight: '',
      bottom: words.slice(1).join(' '),
    }
  }

  const firstNames = presenterNames.value.map((name) => name.split(/\s+/)[0]).filter(Boolean)
  const leftPart = firstNames.slice(0, -1).join(' & ')
  const rightPart = firstNames.at(-1) || ''

  return {
    topSingle: '',
    topLeft: leftPart ? `${leftPart} &` : '&',
    topRight: rightPart,
    bottom: '',
  }
})

const hasMultiplePresenters = computed(() => presenterNames.value.length > 1)
const firstLineText = computed(() => lineTexts.value.topSingle)
const firstLineLeftText = computed(() => lineTexts.value.topLeft)
const firstLineRightText = computed(() => lineTexts.value.topRight)
const secondLineText = computed(() => lineTexts.value.bottom)
const backgroundVideo = computed(() => props.item?.properties?.backgroundVideo || props.item?.properties?.video || DefaultBackgroundVideo)

const getSplitX = (letters, sideOffset) => (index) => {
  const center = (letters.length - 1) / 2
  return index <= center ? -sideOffset : sideOffset
}

const getWaveY = (letters) => (index) => {
  const center = (letters.length - 1) / 2
  const normalizedDistance = Math.abs(index - center) / (center || 1)
  return -600 + Math.sin(normalizedDistance * Math.PI) * 700 + normalizedDistance * 300
}

const doSlideOut = () => {
  const topLeftLetters = textTopLeftEls.value || []
  const topRightLetters = textTopRightEls.value || []
  const bottomShadowLetters = shadowBottomEls.value || []
  const bottomTextLetters = textBottomEls.value || []
  const sideOffset = window.innerWidth + 400

  const timeline = gsapTimeline({ name: 'CurrentProgramFull SlideOut' })

  if (secondLineText.value) {
    // Witte letters van tweede lijn eerst (rechts naar links)
    timeline.to(
      bottomTextLetters,
      {
        x: -96,
        y: 56,
        scale: 0.28,
        transformOrigin: 'left bottom',
        autoAlpha: 0,
        duration: 0.22,
        stagger: { each: 0.04, from: 'end' },
        ease: 'expo.in',
      },
      0
    )
    // Donkerblauwe shadow daarna (rechts naar links)
    timeline.to(
      bottomShadowLetters,
      {
        x: -96,
        y: 56,
        scale: 0.28,
        transformOrigin: 'left bottom',
        autoAlpha: 0,
        duration: 0.22,
        stagger: { each: 0.04, from: 'end' },
        ease: 'expo.in',
      },
      0.05
    )
  }

  if (hasMultiplePresenters.value) {
    timeline
      .to(
        topLeftLetters,
        {
          x: getSplitX(topLeftLetters, sideOffset),
          y: -130,
          scale: 0.4,
          transformOrigin: 'center bottom',
          autoAlpha: 0,
          duration: 0.36,
          stagger: { each: 0.03, from: 'edges' },
          ease: 'expo.in',
        },
        0.08
      )
      .to(
        topRightLetters,
        {
          x: getSplitX(topRightLetters, sideOffset),
          y: -130,
          scale: 0.4,
          transformOrigin: 'center bottom',
          autoAlpha: 0,
          duration: 0.36,
          stagger: { each: 0.03, from: 'edges' },
          ease: 'expo.in',
        },
        0.08
      )
  } else {
    // Eerste lijn: wave naar de zijkanten (randen eerst, dan midden)
    timeline.to(
      topLeftLetters,
      {
        x: getSplitX(topLeftLetters, sideOffset),
        y: getWaveY(topLeftLetters),
        scale: 0.4,
        transformOrigin: 'center bottom',
        autoAlpha: 0,
        duration: 0.4,
        stagger: { each: 0.03, from: 'edges' },
        ease: 'expo.in',
      },
      0.08
    )
  }

  return timeline
}

const adjustTopLineFontSize = async () => {
  const maxFontSizeVw = 30.0
  const minFontSizeVw = 13.0
  const stepVw = 0.2

  topLineFontSizeVw.value = maxFontSizeVw
  await nextTick()

  const container = topLineContainer.value
  if (!container) return

  while (container.scrollWidth > container.clientWidth && topLineFontSizeVw.value > minFontSizeVw) {
    topLineFontSizeVw.value = Math.max(minFontSizeVw, topLineFontSizeVw.value - stepVw)
    await nextTick()
  }
}

const doAppear = async () => {
  await adjustTopLineFontSize()

  // Fade in and animate background video with breathing effect
  if (bgImage.value) {
    gsapTimeline({ name: 'Background Animation', repeat: -1 })
      .fromTo(bgImage.value, { opacity: 0, scale: 1 }, { opacity: 1, duration: 1, ease: 'power2.out' }, 0)
      .to(bgImage.value, { scale: 1.1, duration: 10, ease: 'sine.inOut' }, 0.5)
      .to(bgImage.value, { scale: 1, duration: 10, ease: 'sine.inOut' }, 10.5)
  }

  const topLeftLetters = textTopLeftEls.value || []
  const topRightLetters = textTopRightEls.value || []
  const bottomShadowLetters = shadowBottomEls.value || []
  const bottomTextLetters = textBottomEls.value || []
  const sideOffset = window.innerWidth + 400
  const firstLineStart = 0.1
  const secondLineShadowStart = 0.28
  const secondLineTextStart = 0.33

  const setupTimeline = gsapTimeline({ name: 'CurrentProgramFull Setup' })

  // Only pre-hide targets that actually have letters; GSAP warns ("target not found") on empty targets.
  // With a single presenter the right-hand line is never rendered, so its ref stays empty.
  const hideBeforeAppear = (letters) => {
    if (letters.length) setupTimeline.set(letters, { autoAlpha: 0 })
  }

  hideBeforeAppear(topLeftLetters)
  hideBeforeAppear(topRightLetters)

  if (secondLineText.value) {
    hideBeforeAppear(bottomShadowLetters)
    hideBeforeAppear(bottomTextLetters)
  }

  const appearTimeline = gsapTimeline({ name: 'CurrentProgramFull Appear' })

  if (hasMultiplePresenters.value) {
    appearTimeline
      .fromTo(
        topLeftLetters,
        {
          x: getSplitX(topLeftLetters, sideOffset),
          y: -140,
          scale: 0.38,
          transformOrigin: 'center bottom',
          autoAlpha: 0,
        },
        {
          x: 0,
          y: 0,
          scale: 1,
          autoAlpha: 1,
          duration: 0.38,
          stagger: { each: 0.03, from: 'center' },
          ease: 'expo.out',
        },
        firstLineStart
      )
      .fromTo(
        topRightLetters,
        {
          x: getSplitX(topRightLetters, sideOffset),
          y: -140,
          scale: 0.38,
          transformOrigin: 'center bottom',
          autoAlpha: 0,
        },
        {
          x: 0,
          y: 0,
          scale: 1,
          autoAlpha: 1,
          duration: 0.38,
          stagger: { each: 0.03, from: 'center' },
          ease: 'expo.out',
        },
        firstLineStart + 0.04
      )
  } else {
    appearTimeline.fromTo(
      topLeftLetters,
      {
        x: getSplitX(topLeftLetters, sideOffset),
        y: getWaveY(topLeftLetters),
        scale: 0.4,
        transformOrigin: 'center bottom',
        autoAlpha: 0,
      },
      {
        x: 0,
        y: 0,
        scale: 1,
        autoAlpha: 1,
        duration: 0.4,
        stagger: { each: 0.03, from: 'center' },
        ease: 'expo.out',
      },
      firstLineStart
    )
  }

  if (secondLineText.value) {
    appearTimeline
      .fromTo(
        bottomShadowLetters,
        { x: -96, y: 56, scale: 0.28, transformOrigin: 'left bottom', autoAlpha: 0 },
        {
          x: 0,
          y: 0,
          scale: 1,
          autoAlpha: 1,
          duration: 0.22,
          stagger: { each: 0.04, from: 'start' },
          ease: 'expo.out',
        },
        secondLineShadowStart
      )
      .fromTo(
        bottomTextLetters,
        { x: -96, y: 56, scale: 0.28, transformOrigin: 'left bottom', autoAlpha: 0 },
        {
          x: 0,
          y: 0,
          scale: 1,
          autoAlpha: 1,
          duration: 0.22,
          stagger: { each: 0.04, from: 'start' },
          ease: 'expo.out',
        },
        secondLineTextStart
      )
  }

  // Idle breathing animation - video pulses in and out
  gsapTimeline({ name: 'CurrentProgramFull Breathing', repeat: -1 })
    .to([bgImage], { scale: 1.06, duration: 10, ease: 'sine.inOut', transformOrigin: 'center' }, 2.6)
    .to([bgImage], { scale: 1, duration: 10, ease: 'sine.inOut', transformOrigin: 'center' }, '+=0')
}

const doDisappear = () => {
  // Wind down the component: slide the presenter text out and fade the background video alongside it.
  const timeline = doSlideOut()
  timeline.to(bgImage, { opacity: 0, duration: 0.4, ease: 'power1.in' }, 0.1)
  return timeline
}

onMounted(() => {
  doAppear()
})

watch([firstLineText, firstLineLeftText, firstLineRightText], adjustTopLineFontSize)

if (getCurrentInstance()?.vnode?.key) {
  const { onDisappear } = useDisappearable()
  onDisappear(doDisappear)
}
</script>

<template>
  <div class="relative w-full h-full flex items-center justify-center overflow-hidden" style="background-color: #d00914">
    <VideoPlayer :src="backgroundVideo" autoplay muted playsinline class="absolute inset-0 w-full h-full object-cover opacity-0" @videoElement="bgImage = $event" />

    <div class="relative z-10 flex flex-col items-center leading-none uppercase select-none">
      <div ref="topLineContainer" class="relative overflow-visible w-[98vw] flex justify-center" :style="{ fontSize: `${topLineFontSizeVw}vw` }">
        <div v-if="hasMultiplePresenters" class="flex flex-col items-center leading-[0.88] whitespace-nowrap">
          <SplitCharacters :wrap="false" :text="firstLineLeftText" class="relative font-tondu-beta inline-block" childClass="mx-[0.01em]" style="color: white" @children="textTopLeftEls = $event" />
          <SplitCharacters :wrap="false" :text="firstLineRightText" class="relative font-tondu-beta inline-block" childClass="mx-[0.01em]" style="color: white" @children="textTopRightEls = $event" />
        </div>
        <SplitCharacters
          v-else
          :wrap="false"
          :text="firstLineText"
          class="relative font-tondu-beta whitespace-nowrap inline-block"
          childClass="mx-[0.01em]"
          style="color: white"
          @children="textTopLeftEls = $event"
        />
      </div>

      <div v-if="secondLineText" class="relative overflow-visible mt-[-1.2vw]" style="font-size: 10.5vw">
        <SplitCharacters :text="secondLineText" class="absolute font-tondu-beta" childClass="mx-[0.01em]" style="color: #00123d" @children="shadowBottomEls = $event" />
        <SplitCharacters :text="secondLineText" class="relative font-tondu-beta" childClass="mx-[0.01em]" style="color: white" @children="textBottomEls = $event" />
      </div>
    </div>
  </div>
</template>
